import SEOHead from "@/components/SEOHead";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { motion } from "framer-motion";
import { ArrowLeft, Clock, User, Calendar } from "lucide-react";
import LazyYouTubeEmbed from "@/components/LazyYouTubeEmbed";
import { Link, useParams, Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useEffect } from "react";
import { generateMetaDescription, generatePageTitle } from "@/lib/seoUtils";
import { useArticle, type ContentBlock, type FAQItem } from "@/hooks/use-articles";
import ShareButtons from "@/components/ShareButtons";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase } from "@/integrations/supabase/client";

// Initialize Cal.com
declare global {
  interface Window {
    Cal?: any;
  }
}

const initCalEmbed = () => {
  if (typeof window !== 'undefined' && !window.Cal) {
    (function (C: any, A: string, L: string) {
      let p = function (a: any, ar: any) { a.q.push(ar); };
      let d = C.document;
      C.Cal = C.Cal || function () {
        let cal = C.Cal;
        let ar = arguments;
        if (!cal.loaded) {
          cal.ns = {};
          cal.q = cal.q || [];
          d.head.appendChild(d.createElement("script")).src = A;
          cal.loaded = true;
        }
        if (ar[0] === L) {
          const api = function () { p(api, arguments); };
          const namespace = ar[1];
          api.q = api.q || [];
          if (typeof namespace === "string") {
            cal.ns[namespace] = cal.ns[namespace] || api;
            p(cal.ns[namespace], ar);
            p(cal, ["initNamespace", namespace]);
          } else p(cal, ar);
          return;
        }
        p(cal, ar);
      };
    })(window, "https://app.cal.com/embed/embed.js", "init");

    window.Cal("init", "30min-suivi", { origin: "https://app.cal.com" });
    window.Cal.ns["30min-suivi"]("ui", { "hideEventTypeDetails": false, "layout": "month_view" });
  }
};

// Format date for display
const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  return date.toLocaleDateString("fr-CA", { 
    day: "numeric", 
    month: "short", 
    year: "numeric" 
  });
};

// Loading skeleton component
const ArticleSkeleton = () => (
  <main className="min-h-screen bg-background">
    <Navbar />
    <div className="pt-32 pb-12 relative overflow-hidden">
      <div className="container mx-auto px-6 relative z-10">
        <Skeleton className="h-4 w-48 mb-8" />
        <Skeleton className="h-10 w-32 mb-8" />
        <Skeleton className="h-8 w-24 mb-6" />
        <Skeleton className="h-16 w-3/4 mb-6" />
        <Skeleton className="h-6 w-1/2 mb-8" />
        <div className="flex gap-6">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-24" />
        </div>
      </div>
    </div>
    <div className="container mx-auto px-6">
      <Skeleton className="max-w-4xl mx-auto aspect-video rounded-2xl" />
    </div>
    <div className="py-12">
      <div className="container mx-auto px-6">
        <div className="max-w-3xl mx-auto space-y-4">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-8 w-1/2 mt-8" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-full" />
        </div>
      </div>
    </div>
    <Footer />
  </main>
);

const ArticlePage = () => {
  const { slug } = useParams<{ slug: string }>();
  const { data: article, isLoading, error } = useArticle(slug || "");

  useEffect(() => {
    window.scrollTo(0, 0);
    initCalEmbed();
    if (slug) {
      (supabase as any).rpc("increment_article_view", { _slug: slug });
    }
  }, [slug]);

  // Loading state
  if (isLoading) {
    return <ArticleSkeleton />;
  }

  // Error / not found state
  if (error || !article) {
    return <Navigate to="/articles" replace />;
  }

  const articleUrl = `https://agence8020.com/articles/${slug}`;
  
  // Generate OG image URL
  const ogImageUrl = article.featured_video 
    ? `https://img.youtube.com/vi/${article.featured_video}/maxresdefault.jpg`
    : article.image_url || 'https://agence8020.com/og-image.png';

  // Dynamic SEO metadata generation
  const seoTitle = generatePageTitle(article.meta_title || article.title);
  const seoDescription = generateMetaDescription(
    article.meta_description,
    article.excerpt,
    article.content || []
  );

  // Article JSON-LD Schema
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.excerpt,
    "image": ogImageUrl,
    "datePublished": article.published_date,
    "author": {
      "@type": "Organization",
      "name": article.author || "Agence 8020",
      "url": "https://agence8020.com"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Agence 8020",
      "logo": {
        "@type": "ImageObject",
        "url": "https://agence8020.com/logo.png"
      }
    },
    "mainEntityOfPage": articleUrl
  };

  // Breadcrumb Schema
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Accueil",
        "item": "https://agence8020.com"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Articles",
        "item": "https://agence8020.com/articles"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": article.title,
        "item": articleUrl
      }
    ]
  };

  // FAQ Schema
  const faqSchema = article.faqs && article.faqs.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": article.faqs.map((faq: FAQItem) => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  } : null;

  const content = article.content || [];

  return (
    <>
      <SEOHead
        title={seoTitle}
        description={seoDescription}
        canonical={`/articles/${slug}`}
        ogImage={ogImageUrl}
        schemaJson={[articleSchema, breadcrumbSchema, ...(faqSchema ? [faqSchema] : [])]}
      />

      <main className="min-h-screen bg-background">
        <Navbar />
        
        <article itemScope itemType="https://schema.org/Article">
          {/* Hero Section */}
          <header className="pt-32 pb-12 relative overflow-hidden">
            <div className="absolute inset-0 gradient-hero" />
            <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] gradient-glow animate-pulse-glow" />
            
            <div className="container mx-auto px-6 relative z-10">
              {/* Breadcrumb */}
              <nav aria-label="Fil d'Ariane" className="mb-8">
                <ol className="flex items-center gap-2 text-sm text-muted-foreground">
                  <li><Link to="/" className="hover:text-foreground transition-colors">Accueil</Link></li>
                  <li>/</li>
                  <li><Link to="/articles" className="hover:text-foreground transition-colors">Articles</Link></li>
                  <li>/</li>
                  <li className="text-foreground truncate max-w-[200px]">{article.title}</li>
                </ol>
              </nav>
              
              <Link 
                to="/articles" 
                className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-8"
              >
                <ArrowLeft className="w-4 h-4" aria-hidden="true" />
                Retour aux articles
              </Link>
              
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                <span className="inline-block px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6" itemProp="articleSection">
                  {article.category}
                </span>
                
                <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 max-w-4xl" itemProp="headline">
                  {article.title}
                </h1>
                
                <p className="text-xl text-muted-foreground mb-8 max-w-3xl" itemProp="description">
                  {article.excerpt}
                </p>
                
                <div className="flex flex-wrap items-center gap-6 text-muted-foreground">
                  <span className="flex items-center gap-2" itemProp="author" itemScope itemType="https://schema.org/Organization">
                    <User className="w-4 h-4" aria-hidden="true" />
                    <span itemProp="name">{article.author}</span>
                  </span>
                  <span className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" aria-hidden="true" />
                    <time dateTime={article.published_date || ""} itemProp="datePublished">
                      {formatDate(article.published_date)}
                    </time>
                  </span>
                  <span className="flex items-center gap-2">
                    <Clock className="w-4 h-4" aria-hidden="true" />
                    {article.read_time} de lecture
                  </span>
                </div>

                {/* Share Buttons */}
                <ShareButtons slug={slug || ""} title={article.title} />
              </motion.div>
            </div>
          </header>

          {/* Featured Image/Video */}
          <div className="container mx-auto px-6 -mt-6 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="max-w-4xl mx-auto"
            >
              {article.featured_video ? (
                <div className="w-full rounded-2xl shadow-2xl overflow-hidden">
                  <LazyYouTubeEmbed
                    videoId={article.featured_video}
                    title={article.title}
                    thumbnailQuality="hqdefault"
                    autoplay={false}
                    muted={false}
                    loop={false}
                  />
                </div>
              ) : (
                <img 
                  src={article.image_url || "https://agence8020.com/og-image.png"} 
                  alt={article.title}
                  className="w-full aspect-video object-cover rounded-2xl shadow-2xl"
                  itemProp="image"
                />
              )}
            </motion.div>
          </div>

          {/* Article Content */}
          <section className="py-12">
            <div className="container mx-auto px-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.6 }}
                className="max-w-3xl mx-auto"
                itemProp="articleBody"
              >
                {content.map((item: ContentBlock, index: number) => {
                  if (item.type === "heading") {
                    return (
                      <h2 
                        key={index}
                        className="font-heading text-2xl md:text-3xl font-bold text-foreground mt-10 mb-4"
                      >
                        {item.text}
                      </h2>
                    );
                  } else if (item.type === "video" && item.videoId) {
                    return (
                      <div key={index} className="my-8 aspect-video rounded-xl overflow-hidden shadow-lg">
                        <iframe
                          width="100%"
                          height="100%"
                          src={`https://www.youtube.com/embed/${item.videoId}`}
                          title="YouTube video"
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          allowFullScreen
                          className="w-full h-full"
                        />
                      </div>
                    );
                  } else if (item.type === "list" && item.items) {
                    return (
                      <ul key={index} className="list-disc list-inside text-lg text-muted-foreground leading-relaxed mb-6 space-y-2 pl-4">
                        {item.items.map((listItem: string, listIndex: number) => (
                          <li key={listIndex}>{listItem}</li>
                        ))}
                      </ul>
                    );
                  } else {
                    // Check if text contains HTML (like links)
                    const hasHtml = item.text.includes('<a ') || item.text.includes('<strong>') || item.text.includes('<em>');
                    if (hasHtml) {
                      return (
                        <p 
                          key={index} 
                          className="text-lg text-muted-foreground leading-relaxed mb-6"
                          dangerouslySetInnerHTML={{ __html: item.text }}
                        />
                      );
                    }
                    return (
                      <p 
                        key={index} 
                        className="text-lg text-muted-foreground leading-relaxed mb-6"
                      >
                        {item.text}
                      </p>
                    );
                  }
                })}
              </motion.div>

              {/* FAQ Section */}
              {article.faqs && article.faqs.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4, duration: 0.6 }}
                  className="max-w-3xl mx-auto mt-16"
                >
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground mb-8">
                    FAQ – {article.category}
                  </h2>
                  <div className="space-y-6">
                    {article.faqs.map((faq: FAQItem, index: number) => (
                      <div key={index} className="p-6 rounded-xl bg-card border border-border">
                        <h3 className="font-heading text-lg font-semibold text-foreground mb-3">
                          {faq.question}
                        </h3>
                        <p className="text-muted-foreground leading-relaxed">
                          {faq.answer}
                        </p>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Local SEO */}
              {article.local_seo && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.45, duration: 0.6 }}
                  className="max-w-3xl mx-auto mt-12 p-6 rounded-xl bg-secondary/30 border border-border"
                >
                  <p className="text-muted-foreground text-center">
                    {article.local_seo}
                  </p>
                </motion.div>
              )}
              
              {/* CTA */}
              <motion.aside
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.6 }}
                className="max-w-3xl mx-auto mt-16 p-8 rounded-2xl bg-card border border-border text-center"
                aria-label="Appel à l'action"
              >
                <h3 className="font-heading text-2xl font-bold text-foreground mb-4">
                  On veut générer des leads qualifiés, pas simplement de la visibilité.
                </h3>
                <p className="text-muted-foreground mb-6">
                  Notre accompagnement marketing aide les entrepreneurs québécois à structurer leur contenu et leurs actions pour transformer leur marketing en véritable moteur de croissance.
                </p>
                <Button 
                  variant="hero" 
                  size="lg"
                  asChild
                >
                  <Link to="/accompagnement">
                    Réserver un diagnostic stratégique
                  </Link>
                </Button>
              </motion.aside>
            </div>
          </section>
        </article>

        <Footer />
      </main>
    </>
  );
};

export default ArticlePage;
